import React, { useState, useEffect } from 'react';
import { useAuth } from '@monorepo/shared-auth';
import {
  IconUser, IconMail, IconMapPin, IconCalendar, IconHeart, IconSettings, IconCheck, IconX,
  IconBallBasketball, IconBallFootball, IconBallTennis, IconBallVolleyball, IconBallBaseball,
  IconBallAmericanFootball, IconGolf, IconSwimming, IconRun, IconBike, IconKarate,
  IconTargetArrow, IconTrophy
} from '@tabler/icons-react';
import './Profile.css';

// Icon component mapping - using Tabler Icons from TABLER_REFERENCE.md
const iconComponents = {
  'IconBallBasketball': IconBallBasketball,
  'IconBallFootball': IconBallFootball,
  'IconBallTennis': IconBallTennis,
  'IconBallVolleyball': IconBallVolleyball,
  'IconBallBaseball': IconBallBaseball,
  'IconBallAmericanFootball': IconBallAmericanFootball,
  'IconGolf': IconGolf,
  'IconSwimming': IconSwimming,
  'IconRun': IconRun,
  'IconBike': IconBike,
  'IconKarate': IconKarate,
  'IconTargetArrow': IconTargetArrow,
  'IconTrophy': IconTrophy
};

const Profile = ({ popularActivities = [], siteTheme = 'ubsports' }) => {
  const { currentUser, userProfile, updateUserProfile } = useAuth();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    city: '',
    yearOfBirth: '',
    favoriteSports: [],
    emailNotifications: true,
    marketingEmails: false
  });

  useEffect(() => {
    if (userProfile) {
      setFormData({
        firstName: userProfile.firstName || '',
        lastName: userProfile.lastName || '',
        city: userProfile.city || '',
        yearOfBirth: userProfile.yearOfBirth || '',
        favoriteSports: userProfile.favoriteSports || [],
        emailNotifications: userProfile.emailNotifications ?? true,
        marketingEmails: userProfile.marketingEmails ?? false
      });
    }
  }, [userProfile]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const toggleSport = (sportId) => {
    setFormData(prev => {
      const isSelected = prev.favoriteSports.includes(sportId);
      if (isSelected) {
        return {
          ...prev,
          favoriteSports: prev.favoriteSports.filter(id => id !== sportId)
        };
      } else {
        if (prev.favoriteSports.length >= 3) {
          return prev;
        }
        return {
          ...prev,
          favoriteSports: [...prev.favoriteSports, sportId]
        };
      }
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');

    try {
      await updateUserProfile(currentUser.uid, formData);
      setMessage('Profile updated successfully!');
      setEditing(false);
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage('Failed to update profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      firstName: userProfile.firstName || '',
      lastName: userProfile.lastName || '',
      city: userProfile.city || '',
      yearOfBirth: userProfile.yearOfBirth || '',
      favoriteSports: userProfile.favoriteSports || [],
      emailNotifications: userProfile.emailNotifications ?? true,
      marketingEmails: userProfile.marketingEmails ?? false
    });
    setEditing(false);
    setMessage('');
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 100 }, (_, i) => currentYear - i);

  if (!userProfile) {
    return (
      <div className={`profile-page theme-${siteTheme}`}>
        <div className="profile-loading">Loading profile...</div>
      </div>
    );
  }

  return (
    <div className={`profile-page theme-${siteTheme}`}>
      <div className="profile-container">
        <div className="profile-header">
          <div className="profile-header-content">
            <div className="profile-icon">
              <IconUser size={48} stroke={2} />
            </div>
            <div className="profile-header-text">
              <h1>My Profile</h1>
              <p>{userProfile.email}</p>
            </div>
          </div>
          {!editing && (
            <button
              className="edit-button"
              onClick={() => setEditing(true)}
            >
              <IconSettings size={20} />
              Edit Profile
            </button>
          )}
        </div>

        {message && (
          <div className={`profile-message ${message.includes('success') ? 'success' : 'error'}`}>
            {message}
          </div>
        )}

        <div className="profile-sections">
          {/* Personal Information */}
          <div className="profile-section">
            <h2>Personal Information</h2>
            <div className="profile-grid">
              <div className="profile-field">
                <label>
                  <IconUser size={18} />
                  First Name
                </label>
                {editing ? (
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className="profile-input"
                  />
                ) : (
                  <div className="profile-value">{formData.firstName}</div>
                )}
              </div>

              <div className="profile-field">
                <label>
                  <IconUser size={18} />
                  Last Name
                </label>
                {editing ? (
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className="profile-input"
                  />
                ) : (
                  <div className="profile-value">{formData.lastName}</div>
                )}
              </div>

              <div className="profile-field">
                <label>
                  <IconMail size={18} />
                  Email
                </label>
                <div className="profile-value disabled">{userProfile.email}</div>
              </div>

              <div className="profile-field">
                <label>
                  <IconMapPin size={18} />
                  City
                </label>
                {editing ? (
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    className="profile-input"
                  />
                ) : (
                  <div className="profile-value">{formData.city}</div>
                )}
              </div>

              <div className="profile-field">
                <label>
                  <IconCalendar size={18} />
                  Year of Birth
                </label>
                {editing ? (
                  <select
                    name="yearOfBirth"
                    value={formData.yearOfBirth}
                    onChange={handleInputChange}
                    className="profile-input"
                  >
                    <option value="">Select year</option>
                    {years.map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                ) : (
                  <div className="profile-value">{formData.yearOfBirth}</div>
                )}
              </div>
            </div>
          </div>

          {/* Favorite Sports */}
          <div className="profile-section">
            <h2>
              <IconHeart size={20} />
              Favorite Activities
            </h2>
            {editing ? (
              <>
                <p className="section-description">
                  Select up to 3 activities ({formData.favoriteSports.length}/3 selected)
                </p>
                <div className="sports-grid">
                  {popularActivities.map(activity => {
                    const IconComponent = iconComponents[activity.icon] || IconTrophy;
                    return (
                      <button
                        key={activity.id}
                        type="button"
                        onClick={() => toggleSport(activity.id)}
                        className={`sport-option ${formData.favoriteSports.includes(activity.id) ? 'selected' : ''}`}
                      >
                        <span className="sport-icon">
                          <IconComponent size={24} stroke={2} />
                        </span>
                        <span className="sport-name">{activity.name}</span>
                      </button>
                    );
                  })}
                </div>
              </>
            ) : (
              <div className="selected-sports">
                {formData.favoriteSports.length > 0 ? (
                  formData.favoriteSports.map(sportId => {
                    const sport = popularActivities.find(s => s.id === sportId);
                    if (!sport) return null;
                    const IconComponent = iconComponents[sport.icon] || IconTrophy;
                    return (
                      <div key={sportId} className="selected-sport">
                        <span className="sport-icon">
                          <IconComponent size={20} stroke={2} />
                        </span>
                        <span>{sport.name}</span>
                      </div>
                    );
                  })
                ) : (
                  <div className="no-data">No activities selected</div>
                )}
              </div>
            )}
          </div>

          {/* Preferences */}
          <div className="profile-section">
            <h2>
              <IconSettings size={20} />
              Preferences
            </h2>
            <div className="preferences-list">
              <label className="preference-item">
                <div className="preference-info">
                  <span className="preference-label">Email Notifications</span>
                  <span className="preference-description">
                    Receive updates about challenges, achievements, and new content
                  </span>
                </div>
                {editing ? (
                  <input
                    type="checkbox"
                    name="emailNotifications"
                    checked={formData.emailNotifications}
                    onChange={handleInputChange}
                    className="preference-checkbox"
                  />
                ) : (
                  <span className={`preference-status ${formData.emailNotifications ? 'enabled' : 'disabled'}`}>
                    {formData.emailNotifications ? 'Enabled' : 'Disabled'}
                  </span>
                )}
              </label>

              <label className="preference-item">
                <div className="preference-info">
                  <span className="preference-label">Marketing Emails</span>
                  <span className="preference-description">
                    Receive promotional content, tips, and special offers
                  </span>
                </div>
                {editing ? (
                  <input
                    type="checkbox"
                    name="marketingEmails"
                    checked={formData.marketingEmails}
                    onChange={handleInputChange}
                    className="preference-checkbox"
                  />
                ) : (
                  <span className={`preference-status ${formData.marketingEmails ? 'enabled' : 'disabled'}`}>
                    {formData.marketingEmails ? 'Enabled' : 'Disabled'}
                  </span>
                )}
              </label>
            </div>
          </div>
        </div>

        {editing && (
          <div className="profile-actions">
            <button
              onClick={handleCancel}
              className="action-button cancel"
              disabled={saving}
            >
              <IconX size={20} />
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="action-button save"
              disabled={saving}
            >
              <IconCheck size={20} />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;
